import * as React from "react";
import { Box, Typography, Stack, LinearProgress } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { formatLatLon, type AisScan } from "./aisScan";
import type { GlacierCapture } from "./glacierCapture";
import { formatDataSize, type PayloadResult } from "./payloadResults";
import { DataField, MCS_COLORS, MONO, Panel, StatusLamp } from "./mcsUi";
import { PAYLOAD_TM, type PayloadState } from "./TelemetryPanel";

function Progress(props: { value: number; color: string }) {
  return (
    <LinearProgress
      variant="determinate"
      value={Math.min(100, Math.max(0, props.value * 100))}
      sx={{
        height: 8,
        borderRadius: 4,
        bgcolor: "rgba(255,255,255,0.08)",
        "& .MuiLinearProgress-bar": { bgcolor: props.color, borderRadius: 4 },
      }}
    />
  );
}

const cellSx = {
  fontFamily: MONO,
  fontSize: 11.5,
  color: "rgba(226,232,240,0.85)",
  py: 0.6,
  pr: 1.5,
  whiteSpace: "nowrap" as const,
};
const headSx = {
  ...cellSx,
  color: MCS_COLORS.label,
  fontWeight: 700,
  letterSpacing: "0.08em",
  fontSize: 10,
};

// ICEWATCH: the glacier front now vs. a year ago, and how fast the
// watched glaciers are retreating.
function GlacierResult(props: { capture: GlacierCapture }) {
  const { capture } = props;
  const fastest = capture.sites[0];
  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        <DataField label="Target" value={capture.target.name} title={`${capture.target.name}, ${capture.target.region}`} />
        <DataField label="Images" value={`${capture.frames} × ${capture.bands.length} bands`} title={capture.bands.join(", ")} />
        <DataField
          label="Cloud cover"
          value={`${capture.cloudCoverPct}%`}
          color={capture.cloudCoverPct > 30 ? MCS_COLORS.warn : MCS_COLORS.ok}
        />
        <DataField label={`Retreat since ${capture.referenceYear}`} value={`${capture.retreatM} m`} color={MCS_COLORS.alarm} />
      </Box>

      <Box
        component="img"
        src={capture.imageUrl}
        alt={`${capture.target.name}: the glacier front in ${capture.referenceYear} and ${capture.currentYear}, ${capture.retreatM} m apart`}
        sx={{ display: "block", width: "100%", height: "auto", borderRadius: 1.5, border: "1px solid rgba(255,255,255,0.1)" }}
      />

      <Typography
        sx={{
          fontSize: 12.5,
          color: "rgba(226,232,240,0.85)",
          lineHeight: 1.55,
        }}
      >
        Compared with the reference image from {capture.referenceYear}, the front of {capture.target.name} has moved {capture.retreatM} m inland along
        its {capture.frontWidthKm} km wide front: about {capture.areaLossKm2} km² of ice lost to the sea in one year.
      </Typography>

      <Stack
        direction="row"
        spacing={1.25}
        alignItems="flex-start"
        sx={{
          p: 1.25,
          borderRadius: 1.5,
          border: "1px solid rgba(248,113,113,0.4)",
          bgcolor: "rgba(248,113,113,0.08)",
        }}
      >
        <WarningAmberRoundedIcon sx={{ color: MCS_COLORS.alarm, fontSize: 20, mt: 0.1 }} />
        <Typography
          sx={{
            fontSize: 12.5,
            color: "rgba(226,232,240,0.88)",
            lineHeight: 1.5,
          }}
        >
          <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700, color: "#FFFFFF" }}>
            Fastest-changing site: {fastest.name} ({fastest.region})
          </Box>
          <br />
          Retreating about {fastest.retreatMPerYear} m per year, the most of the glaciers this mission watches.
        </Typography>
      </Stack>

      <Box
        sx={{
          overflowX: "auto",
          borderRadius: 1.5,
          border: "1px solid rgba(255,255,255,0.08)",
          bgcolor: "rgba(3,2,6,0.5)",
          px: 1.25,
          py: 0.5,
        }}
      >
        <Box component="table" sx={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              {["Glacier", "Region", "Front retreat / year"].map((h) => (
                <Box component="th" key={h} sx={{ ...headSx, textAlign: "left" }}>
                  {h}
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {capture.sites.map((site, i) => (
              <Box component="tr" key={site.name} sx={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <Box
                  component="td"
                  sx={{
                    ...cellSx,
                    color: i === 0 ? MCS_COLORS.alarm : "#FFFFFF",
                  }}
                >
                  {site.name}
                  {site.name === capture.target.name ? " (imaged now)" : ""}
                </Box>
                <Box component="td" sx={cellSx}>
                  {site.region}
                </Box>
                <Box
                  component="td"
                  sx={{
                    ...cellSx,
                    color: i === 0 ? MCS_COLORS.alarm : cellSx.color,
                  }}
                >
                  {site.retreatMPerYear} m
                </Box>
              </Box>
            ))}
          </tbody>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 11, color: "rgba(226,232,240,0.45)" }}>
        The glaciers are real; the measurements are simulated for the workshop.
      </Typography>
    </Stack>
  );
}

// What the payload delivers after the command: switched on, then working
// over the target area after this pass (out of contact), the data stored on
// board, its downlink at the next pass, and finally the result -- for
// GHOSTSHIP the ships heard and the dark ones that weren't, for ICEWATCH
// the glacier images and how far the ice has retreated.
export default function PayloadPanel(props: {
  payloadName: string;
  state: PayloadState;
  result: PayloadResult | null;
  recording: number;
  downlink: number;
  inContact: boolean;
}) {
  const { payloadName, state, result, recording, downlink, inContact } = props;
  const tm = PAYLOAD_TM[state];
  const text = (t: string) => <Typography sx={{ color: "rgba(226,232,240,0.78)", fontSize: 13, lineHeight: 1.6 }}>{t}</Typography>;
  const doing = result?.kind === "ais" ? "records every AIS signal it hears" : "takes images of the glacier front";

  return (
    <Panel
      title={`Payload Data · ${payloadName}`}
      right={<StatusLamp color={tm.color} label={tm.label} pulsing={state === "recording" || state === "downlinking"} />}
    >
      {!result ? (
        text(
          state === "off"
            ? "Payload data appears here once your command has been executed."
            : "Your command was executed. This payload delivers no data to show here.",
        )
      ) : state === "off" ? (
        text(`Once the command is executed, the ${payloadName} switches on; over ${result.areaName} it ${doing}.`)
      ) : state === "scheduled" ? (
        text(
          `The ${payloadName} is on. After this pass the satellite flies over ${result.areaName} and ${doing}. ` +
            "It can only send the data down at the next pass over your ground station.",
        )
      ) : state === "recording" ? (
        <Stack spacing={1.25}>
          {text(
            `Over ${result.areaName}: the ${payloadName} ${doing}, storing it all on board. No contact with the ground right now.`,
          )}
          <Progress value={recording} color={MCS_COLORS.ok} />
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 12,
              color: "rgba(226,232,240,0.7)",
            }}
          >
            {Math.round(result.units * recording)} {result.unitLabel} recorded
          </Typography>
        </Stack>
      ) : state === "stored" ? (
        text(
          `${formatDataSize(result.dataKb)} of ${result.unitLabel} stored on board. The downlink starts as soon as the station has locked onto the signal.`,
        )
      ) : state === "downlinking" ? (
        <Stack spacing={1.25}>
          {text(inContact ? `Receiving the stored ${result.unitLabel}…` : "Contact lost: the downlink continues at the next pass.")}
          <Progress value={downlink} color={MCS_COLORS.info} />
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 12,
              color: "rgba(226,232,240,0.7)",
            }}
          >
            {formatDataSize(result.dataKb * downlink)} / {formatDataSize(result.dataKb)}
          </Typography>
        </Stack>
      ) : result.kind === "imaging" ? (
        <GlacierResult capture={result.data} />
      ) : (
        <AisResult scan={result.data} />
      )}
    </Panel>
  );
}

// GHOSTSHIP: the ships heard, and the dark ones that weren't.
function AisResult(props: { scan: AisScan }) {
  const { scan } = props;
  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        <DataField label="Area" value={scan.area.name} title={scan.area.name} />
        <DataField label="AIS messages" value={scan.totalMessages.toLocaleString("en-US")} />
        <DataField label="Ships heard" value={scan.vessels.filter((v) => !v.dark).length} color={MCS_COLORS.ok} />
        <DataField label="Dark ships" value={scan.vessels.filter((v) => v.dark).length} color={MCS_COLORS.alarm} />
      </Box>

      <Stack spacing={1}>
        {scan.vessels
          .filter((v) => v.dark)
          .map((v) => (
            <Stack
              key={v.mmsi}
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                border: "1px solid rgba(248,113,113,0.4)",
                bgcolor: "rgba(248,113,113,0.08)",
              }}
            >
              <WarningAmberRoundedIcon sx={{ color: MCS_COLORS.alarm, fontSize: 20, mt: 0.1 }} />
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: "rgba(226,232,240,0.88)",
                  lineHeight: 1.5,
                }}
              >
                <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700, color: "#FFFFFF" }}>
                  {v.name} · MMSI {v.mmsi} · {v.flag} · {v.type}
                </Box>
                <br />
                Last heard {v.lastHeardHoursAgo} h ago near {formatLatLon(v.latDeg, v.lonDeg)}, heading into the area, but no AIS signal
                during this scan. Possible ghost ship: its transponder may have been switched off.
              </Typography>
            </Stack>
          ))}
      </Stack>

      <Box
        sx={{
          overflowX: "auto",
          borderRadius: 1.5,
          border: "1px solid rgba(255,255,255,0.08)",
          bgcolor: "rgba(3,2,6,0.5)",
          px: 1.25,
          py: 0.5,
        }}
      >
        <Box component="table" sx={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              {["MMSI", "Name", "Flag", "Type", "Position", "Speed", "Messages"].map((h) => (
                <Box component="th" key={h} sx={{ ...headSx, textAlign: "left" }}>
                  {h}
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {scan.vessels.map((v) => (
              <Box component="tr" key={v.mmsi} sx={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <Box component="td" sx={cellSx}>
                  {v.mmsi}
                </Box>
                <Box
                  component="td"
                  sx={{
                    ...cellSx,
                    color: v.dark ? MCS_COLORS.alarm : "#FFFFFF",
                  }}
                >
                  {v.name}
                </Box>
                <Box component="td" sx={cellSx}>
                  {v.flag}
                </Box>
                <Box component="td" sx={cellSx}>
                  {v.type}
                </Box>
                <Box component="td" sx={cellSx}>
                  {formatLatLon(v.latDeg, v.lonDeg)}
                </Box>
                <Box component="td" sx={cellSx}>
                  {v.dark ? "—" : `${v.speedKn} kn`}
                </Box>
                <Box
                  component="td"
                  sx={{
                    ...cellSx,
                    color: v.dark ? MCS_COLORS.alarm : cellSx.color,
                  }}
                >
                  {v.dark ? "silent" : v.messages}
                </Box>
              </Box>
            ))}
          </tbody>
        </Box>
      </Box>
    </Stack>
  );
}
